import { type AdminUserCompanyTagDto, UserProfileRole } from '@@types/api.types';
import { EditSectionControls } from '@components/shared/edit-section-controls.component';
import { SectionCard } from '@components/shared/section-card.component';
import { Badge } from '@components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { HelpCircleIcon, XIcon } from 'lucide-react';
import React from 'react';
import { useAdminUserAccessControlsForm } from '../hooks/use-admin-user-access-controls-form.hook';
import { AdminUsersModalOrganizationAccessField } from './admin-users-modal-organization-access-field.component';

type AdminUsersModalTagsProps = {
  isBusy: boolean;
  organizationBlacklist?: string[];
  organizationWhitelist?: string[];
  userId: string;
  userRole?: UserProfileRole;
  userTags?: AdminUserCompanyTagDto[];
};

type AdminUsersModalTagsContentProps = {
  isBusy: boolean;
  organizationBlacklist?: string[];
  organizationWhitelist?: string[];
  userId: string;
  userTags: AdminUserCompanyTagDto[];
};

export const AdminUsersModalTags: React.FC<AdminUsersModalTagsProps> = (props) => {
  const { isBusy, organizationBlacklist, organizationWhitelist, userId, userRole, userTags = [] } = props;

  const supportsAccessControls = userRole === UserProfileRole.EXPERT || userRole === UserProfileRole.INTELLIGENCE;

  if (!supportsAccessControls) {
    return null;
  }

  const accessControlsStateKey = [
    userId,
    ...(organizationBlacklist ?? []),
    '--',
    ...(organizationWhitelist ?? []),
    '--',
    ...userTags.map((tag) => tag.name),
  ].join('|');

  return (
    <AdminUsersModalTagsContent
      key={accessControlsStateKey}
      isBusy={isBusy}
      organizationBlacklist={organizationBlacklist}
      organizationWhitelist={organizationWhitelist}
      userId={userId}
      userTags={userTags}
    />
  );
};

const AdminUsersModalTagsContent: React.FC<AdminUsersModalTagsContentProps> = (props) => {
  const { isBusy: externalBusy, organizationBlacklist, organizationWhitelist, userId, userTags } = props;

  const {
    allSelectableTagOptions,
    form,
    handleCancel,
    handleCreateTagOption,
    handleEdit,
    handleOrganizationAccessFieldChange,
    handleSave,
    handleTagNamesFieldChange,
    isBusy,
    isEditing,
    isImplicitOrAllTag,
    maxOrganizationsPerList,
    organizationAccessFieldConfigs,
    organizationBlacklistDisabledTooltip,
    organizationsTagsTooltip,
    resolveOrganizationOption,
    selectedCustomTags,
    selectedImplicitTags,
    selectedTagNames,
  } = useAdminUserAccessControlsForm({
    externalBusy,
    organizationBlacklist,
    organizationWhitelist,
    userId,
    userTags,
  });

  return (
    <SectionCard>
      <EditSectionControls
        cancelAriaLabel="Cancel Access Controls"
        editAriaLabel="Edit Access Controls"
        isCancelDisabled={isBusy}
        isEditing={isEditing}
        isSaveDisabled={isBusy}
        onCancel={handleCancel}
        onEdit={handleEdit}
        onSave={handleSave}
        saveAriaLabel="Save Access Controls"
      />
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Access Controls</h3>
        </div>

        <form.AppForm>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-gray-500">Organizations Tags</span>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircleIcon className="size-3.5 cursor-help text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs font-normal text-xs">
                      <p>{organizationsTagsTooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {!isEditing && selectedTagNames.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedImplicitTags.map((implicitTag) => (
                    <Badge
                      key={`implicit-${implicitTag.tagName}`}
                      size="sm"
                      variant="secondary"
                    >
                      {implicitTag.label}
                    </Badge>
                  ))}
                  {selectedCustomTags.map((tagName) => (
                    <Badge
                      key={`custom-${tagName}`}
                      size="sm"
                      variant="innovator"
                    >
                      {tagName}
                    </Badge>
                  ))}
                </div>
              ) : (
                !isEditing && <span className="text-sm text-gray-500">No organization tags assigned</span>
              )}

              {isEditing && (
                <form.AppField
                  listeners={{
                    onChange: ({ value }) => {
                      handleTagNamesFieldChange(value);
                    },
                  }}
                  name="tagNames"
                >
                  {(field) => (
                    <field.SelectMultipleField
                      disabled={isBusy}
                      onCreateOption={(tagName) => {
                        handleCreateTagOption(tagName);
                      }}
                      options={allSelectableTagOptions}
                      placeholder="Select or type tags..."
                      renderBadge={(params) => {
                        const { label, remove } = params;
                        const isImplicitTag = isImplicitOrAllTag(label);
                        return (
                          <Badge
                            className={
                              isImplicitTag
                                ? 'group/tag border-gray-300 bg-white text-gray-900 hover:bg-white'
                                : 'group/tag border-transparent'
                            }
                            size="xs"
                            variant={isImplicitTag ? 'outline' : 'innovator'}
                          >
                            {label}
                            <button
                              className="ml-1 rounded-full hover:bg-gray-200/50"
                              disabled={isBusy}
                              onClick={(event) => {
                                event.stopPropagation();
                                remove();
                              }}
                              type="button"
                            >
                              <XIcon className="size-3" />
                            </button>
                          </Badge>
                        );
                      }}
                    />
                  )}
                </form.AppField>
              )}
            </div>

            {organizationAccessFieldConfigs.map((organizationAccessFieldConfig) => {
              const { disabled, emptyText, fieldName, options, organizationIds, title, tooltip } =
                organizationAccessFieldConfig;

              const selectField = isEditing ? (
                <form.AppField
                  listeners={{
                    onChange: ({ value }) => {
                      handleOrganizationAccessFieldChange({
                        fieldName,
                        value,
                      });
                    },
                  }}
                  name={fieldName}
                >
                  {(field) => (
                    <field.SelectMultipleField
                      disabled={isBusy || disabled}
                      options={options}
                      placeholder="Search and select organizations..."
                    />
                  )}
                </form.AppField>
              ) : undefined;

              const editField =
                disabled && isEditing ? (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-full">{selectField}</div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs font-normal text-xs">
                        <p>{organizationBlacklistDisabledTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  selectField
                );

              return (
                <AdminUsersModalOrganizationAccessField
                  key={fieldName}
                  editField={editField}
                  emptyText={emptyText}
                  isEditing={isEditing}
                  maxItems={maxOrganizationsPerList}
                  organizationIds={organizationIds}
                  resolveOrganizationOption={resolveOrganizationOption}
                  title={title}
                  tooltip={tooltip}
                />
              );
            })}
          </div>
        </form.AppForm>
      </div>
    </SectionCard>
  );
};
